import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { createPhasesBlock } from 'Cube/view/layouts/quote/QuoteEditorContent/utils/createPhasesBlock'
import { useCallback, useEffect, useState } from 'react'

/**
 * Gives the quote a pricing block when it hasn't got one, so an amendment
 * always shows the schedule it is amending. Without one the new pricing has
 * nowhere to appear - not in the published quote, and not even in the editor,
 * where the pricing table only renders inside that block.
 *
 * The block holds a copy of the quote's pricing, so asking for one only queues
 * it. The copy is taken in the effect, once the amendment has been through the
 * reducer. Nothing else may update the quote in between, or the copy would be
 * of the pricing the amendment has just replaced.
 */
export const useAddPricingBlock = (): (() => void) => {
  const cubeContext = useCubeContext()
  const [isQueued, setIsQueued] = useState(false)

  useEffect(() => {
    if (!isQueued) {
      return
    }

    setIsQueued(false)

    const presentation = cubeContext.queries.rawData.data.presentation

    if (presentation.blocks.some(block => block.blockType === 'phases')) {
      return
    }

    // Above the rest of the content, where a quote built from a template has
    // it. Appending would leave the pricing below the terms.
    cubeContext.mutators.updateData({
      presentation: {
        ...presentation,
        blocks: [createPhasesBlock(cubeContext.queries), ...presentation.blocks]
      }
    })

    /**
     * The editor only reads the presentation as it starts up, so without the
     * restart the block wouldn't appear in the editor - and the editor's next
     * sync back to the quote would quietly take it out again. The restart costs
     * the editor's undo history, which is why it only happens when a block is
     * actually added.
     */
    cubeContext.mutators.updateEditor({
      quoteEditorNeedsReinitialization: true
    })
  }, [isQueued, cubeContext])

  return useCallback(() => setIsQueued(true), [])
}
